import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiAuthHeaders, apiFetch, apiUrl } from '@shared/api/http'
import {
  MembersAreaDetailSchema,
  MembersAssetSchema,
  MembersLessonSchema,
  MembersModuleSchema,
  MembersUploadUrlSchema,
  MembersVideoSchema,
  MembersVideoUploadSchema,
} from './members-area.contract'
import type {
  MembersAreaDetail,
  MembersAsset,
  MembersContentKind,
  MembersContentStatus,
  MembersLesson,
  MembersLessonKind,
  MembersModule,
  MembersReleaseRule,
  MembersUploadUrl,
} from './members-area.contract'
import { productKeys } from '@entities/product/api/product.queries'
import { membersAreaKeys } from './members-area.queries'
import { visibleUploadPercent } from './video-upload-progress'

function invalidateContent(queryClient: ReturnType<typeof useQueryClient>, productId: string) {
  void queryClient.invalidateQueries({ queryKey: membersAreaKeys.lists() })
  void queryClient.invalidateQueries({ queryKey: membersAreaKeys.detail(productId) })
}

export function usePatchMembersCover(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (coverImage: string) => {
      await apiFetch(`/catalog/products/${productId}`, {
        method: 'PATCH',
        body: { coverImage },
      })
      return coverImage
    },
    onSuccess: (coverImage) => {
      queryClient.setQueryData<MembersAreaDetail>(membersAreaKeys.detail(productId), (current) =>
        current ? { ...current, coverImage } : current,
      )
      invalidateContent(queryClient, productId)
      void queryClient.invalidateQueries({ queryKey: productKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) })
    },
  })
}

export function usePatchMembersContent(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { kind?: MembersContentKind; status?: MembersContentStatus }) => {
      const data = await apiFetch<unknown>(`/members-area/products/${productId}`, {
        method: 'PATCH',
        body,
      })
      return MembersAreaDetailSchema.parse(data)
    },
    onSuccess: () => invalidateContent(queryClient, productId),
  })
}

export function useCreateMembersModule(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (title?: string) => {
      const data = await apiFetch<unknown>(`/members-area/products/${productId}/modules`, {
        method: 'POST',
        body: title ? { title } : {},
      })
      return MembersModuleSchema.parse(data)
    },
    onSuccess: () => invalidateContent(queryClient, productId),
  })
}

export function useUpdateMembersModule(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { moduleId: string; title: string }) => {
      const data = await apiFetch<unknown>(
        `/members-area/products/${productId}/modules/${input.moduleId}`,
        { method: 'PATCH', body: { title: input.title } },
      )
      return data
    },
    onSuccess: () => invalidateContent(queryClient, productId),
  })
}

export function useDeleteMembersModule(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (moduleId: string) => {
      await apiFetch(`/members-area/products/${productId}/modules/${moduleId}`, {
        method: 'DELETE',
      })
    },
    onSuccess: () => invalidateContent(queryClient, productId),
  })
}

export function useOrderMembersModules(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (orderedIds: string[]) => {
      await apiFetch(`/members-area/products/${productId}/modules/order`, {
        method: 'PUT',
        body: { orderedIds },
      })
    },
    onSuccess: () => invalidateContent(queryClient, productId),
  })
}

export function useCreateMembersLesson(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { moduleId: string; title?: string; kind?: MembersLessonKind }) => {
      const data = await apiFetch<unknown>(
        `/members-area/products/${productId}/modules/${input.moduleId}/lessons`,
        {
          method: 'POST',
          body: {
            ...(input.title !== undefined ? { title: input.title } : {}),
            ...(input.kind !== undefined ? { kind: input.kind } : {}),
          },
        },
      )
      return MembersLessonSchema.parse(data)
    },
    onSuccess: () => invalidateContent(queryClient, productId),
  })
}

export function useUpdateMembersLesson(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      moduleId: string
      lessonId: string
      title?: string
      kind?: MembersLessonKind
      body?: string
      releaseRule?: MembersReleaseRule
      status?: MembersLesson['status']
    }) => {
      const data = await apiFetch<unknown>(
        `/members-area/products/${productId}/modules/${input.moduleId}/lessons/${input.lessonId}`,
        {
          method: 'PATCH',
          body: {
            ...(input.title !== undefined ? { title: input.title } : {}),
            ...(input.kind !== undefined ? { kind: input.kind } : {}),
            ...(input.body !== undefined ? { body: input.body } : {}),
            ...(input.releaseRule !== undefined ? { releaseRule: input.releaseRule } : {}),
            ...(input.status !== undefined ? { status: input.status } : {}),
          },
        },
      )
      return data
    },
    onSuccess: () => invalidateContent(queryClient, productId),
  })
}

export function useDeleteMembersLesson(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { moduleId: string; lessonId: string }) => {
      await apiFetch(
        `/members-area/products/${productId}/modules/${input.moduleId}/lessons/${input.lessonId}`,
        { method: 'DELETE' },
      )
    },
    onSuccess: () => invalidateContent(queryClient, productId),
  })
}

export function useOrderMembersLessons(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { moduleId: string; orderedIds: string[] }) => {
      await apiFetch(
        `/members-area/products/${productId}/modules/${input.moduleId}/lessons/order`,
        { method: 'PUT', body: { orderedIds: input.orderedIds } },
      )
    },
    onSuccess: () => invalidateContent(queryClient, productId),
  })
}

export function useCreateMembersUploadUrl(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      fileName: string
      contentType: string
      sizeBytes: number
      lessonId?: string
    }) => {
      const data = await apiFetch<unknown>(
        `/members-area/products/${productId}/assets/upload-url`,
        {
          method: 'POST',
          body: input,
        },
      )
      return MembersUploadUrlSchema.parse(data)
    },
    onSuccess: () => invalidateContent(queryClient, productId),
  })
}

export function useConfirmMembersAsset(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (assetId: string) => {
      const data = await apiFetch<unknown>(
        `/members-area/products/${productId}/assets/${assetId}/confirm`,
        { method: 'POST' },
      )
      return MembersAssetSchema.parse(data)
    },
    onSuccess: () => invalidateContent(queryClient, productId),
  })
}

export function useDeleteMembersAsset(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (assetId: string) => {
      await apiFetch(`/members-area/products/${productId}/assets/${assetId}`, { method: 'DELETE' })
    },
    onSuccess: () => invalidateContent(queryClient, productId),
  })
}

export function useOrderMembersAssets(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { orderedIds: string[]; lessonId?: string }) => {
      await apiFetch(`/members-area/products/${productId}/assets/order`, {
        method: 'PUT',
        body: input,
      })
    },
    onSuccess: () => invalidateContent(queryClient, productId),
  })
}

export async function putMembersAreaFile(upload: MembersUploadUrl, file: File, productId: string) {
  if (isRemoteUploadUrl(upload.uploadUrl)) {
    const remote = await fetch(upload.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': upload.contentType },
      body: file,
    })
    if (!remote.ok) {
      throw new Error('Não foi possível enviar o arquivo.')
    }
    return
  }
  const sourceUrl = apiUrl(`/members-area/products/${productId}/assets/${upload.id}/source`)
  const headers = new Headers({ 'Content-Type': upload.contentType })
  const auth = apiAuthHeaders(sourceUrl)
  if (auth) {
    for (const [name, value] of Object.entries(auth)) headers.set(name, value)
  }
  const response = await fetch(sourceUrl, { method: 'PUT', headers, body: file })
  if (!response.ok) {
    throw new Error('Não foi possível enviar o arquivo.')
  }
}

function isRemoteUploadUrl(url: string) {
  return url.startsWith('https://') || url.startsWith('http://')
}

export const MAX_VIDEO_BYTES = 1024 * 1024 * 1024
export const VIDEO_ACCEPT = '.mp4,.mov,.webm,video/mp4,video/quicktime,video/webm'

function videoPath(productId: string, lessonId: string) {
  return `/members-area/products/${productId}/lessons/${lessonId}/video`
}

export async function uploadMembersVideo(
  productId: string,
  input: {
    lessonId: string
    file: File
    onProgress: (progress: number) => void
    signal?: AbortSignal
  },
) {
  const contentType = videoFileContentType(input.file)
  const data = await apiFetch<unknown>(`${videoPath(productId, input.lessonId)}/upload-url`, {
    method: 'POST',
    body: {
      fileName: input.file.name,
      contentType,
      sizeBytes: input.file.size,
    },
  })
  const upload = MembersVideoUploadSchema.parse(data)
  await putWithProgress(upload.uploadUrl, input.file, contentType, input.onProgress, input.signal)
  const confirmed = await apiFetch<unknown>(`${videoPath(productId, input.lessonId)}/confirm`, {
    method: 'POST',
  })
  return MembersVideoSchema.parse(confirmed)
}

export function useUploadMembersVideo(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      lessonId: string
      file: File
      onProgress: (progress: number) => void
      signal?: AbortSignal
    }) => uploadMembersVideo(productId, input),
    onSettled: () => invalidateContent(queryClient, productId),
  })
}

export function useRetryMembersVideo(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (lessonId: string) => {
      const data = await apiFetch<unknown>(`${videoPath(productId, lessonId)}/retry`, {
        method: 'POST',
      })
      return MembersVideoSchema.parse(data)
    },
    onSuccess: () => invalidateContent(queryClient, productId),
  })
}

export function useDeleteMembersVideo(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (lessonId: string) => {
      await apiFetch(videoPath(productId, lessonId), { method: 'DELETE' })
    },
    onSuccess: () => invalidateContent(queryClient, productId),
  })
}

function putWithProgress(
  url: string,
  file: File,
  contentType: string,
  onProgress: (progress: number) => void,
  signal?: AbortSignal,
) {
  if (url.startsWith('memory://') || url.startsWith('local://')) {
    return Promise.resolve()
  }
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const abort = () => xhr.abort()
    if (signal?.aborted) {
      reject(new DOMException('Envio cancelado', 'AbortError'))
      return
    }
    signal?.addEventListener('abort', abort, { once: true })
    xhr.open('PUT', url)
    const headers = apiAuthHeaders(url)
    if (headers) {
      for (const [name, value] of Object.entries(headers)) xhr.setRequestHeader(name, value)
    }
    xhr.setRequestHeader('Content-Type', contentType)
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(visibleUploadPercent((event.loaded / event.total) * 100))
      }
    }
    xhr.onload = () => {
      signal?.removeEventListener('abort', abort)
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject(new Error('Não foi possível enviar o vídeo.'))
    }
    xhr.onerror = () => {
      signal?.removeEventListener('abort', abort)
      reject(new Error('Não foi possível enviar o vídeo.'))
    }
    xhr.onabort = () => {
      signal?.removeEventListener('abort', abort)
      reject(new DOMException('Envio cancelado', 'AbortError'))
    }
    xhr.send(file)
  })
}

function videoFileContentType(file: File): string {
  if (file.type) return file.type
  const name = file.name.toLowerCase()
  if (name.endsWith('.mov')) return 'video/quicktime'
  if (name.endsWith('.webm')) return 'video/webm'
  return 'video/mp4'
}

export type { MembersAreaDetail, MembersAsset, MembersLesson, MembersModule }
